import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Package, Plus, Pencil, Trash2, Save, ShieldAlert, TrendingUp, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const emptyForm = {
  company_id: "",
  ramo: "",
  name: "",
  code: "",
  default_commission_percent: "",
  default_broker_commission_percent: "",
  buy_cost: "",
  sell_cost: "",
  description: "",
  is_active: true,
  sort_order: 100,
};

const ROLE_LABELS = {
  admin: "Admin",
  backoffice: "Backoffice",
  agente: "Agente",
  collaboratore: "Collaboratore",
};

// Formattazione EUR italiana (€ 1.234,56)
const fmtEur = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `€ ${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function ProductsCatalog() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterRamo, setFilterRamo] = useState("");
  const [q, setQ] = useState("");

  // Config visibilità margini (per ruolo)
  const [marginConfig, setMarginConfig] = useState({ visible_roles: ["admin"], can_see: false, role_choices: [] });
  const [configOpen, setConfigOpen] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const canSeeMargins = !!marginConfig.can_see;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, co, tr, cfg] = await Promise.all([
        api.get("/products"),
        api.get("/companies"),
        api.get("/tax-rates"),
        api.get("/product-margins-config"),
      ]);
      setItems(p.data || []);
      setCompanies(co.data || []);
      setTaxRates(tr.data || []);
      setMarginConfig(cfg.data || { visible_roles: ["admin"], can_see: false, role_choices: [] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const companyName = (id) => (companies.find((c) => c.id === id) || {}).name || "—";
  const ramoLabel = (r) => r ? r.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "—";

  const filtered = useMemo(() => {
    const qLc = q.trim().toLowerCase();
    return items.filter((p) => {
      if (filterCompany && p.company_id !== filterCompany) return false;
      if (filterRamo && p.ramo !== filterRamo) return false;
      if (qLc && !`${p.name} ${p.code || ""} ${companyName(p.company_id)}`.toLowerCase().includes(qLc)) return false;
      return true;
    });
  }, [items, filterCompany, filterRamo, q, companies]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      company_id: p.company_id || "",
      ramo: p.ramo || "",
      name: p.name || "",
      code: p.code || "",
      default_commission_percent: p.default_commission_percent ?? "",
      default_broker_commission_percent: p.default_broker_commission_percent ?? "",
      buy_cost: p.buy_cost ?? "",
      sell_cost: p.sell_cost ?? "",
      description: p.description || "",
      is_active: p.is_active !== false,
      sort_order: p.sort_order || 100,
    });
    setOpen(true);
  };

  const parseNumOrNull = (s) => (s === "" || s === null || s === undefined ? null : parseFloat(s));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome prodotto obbligatorio"); return; }
    if (!form.company_id) { toast.error("Seleziona una compagnia"); return; }
    if (!form.ramo) { toast.error("Seleziona un ramo"); return; }
    const payload = {
      ...form,
      default_commission_percent: parseNumOrNull(form.default_commission_percent),
      default_broker_commission_percent: parseNumOrNull(form.default_broker_commission_percent),
      buy_cost: canSeeMargins ? parseNumOrNull(form.buy_cost) : null,
      sell_cost: canSeeMargins ? parseNumOrNull(form.sell_cost) : null,
      sort_order: parseInt(form.sort_order, 10) || 100,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success(`Prodotto "${form.name}" aggiornato`);
      } else {
        await api.post("/products", payload);
        toast.success(`Prodotto "${form.name}" aggiunto`);
      }
      setOpen(false); setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare il prodotto "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Prodotto eliminato");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleConfigRole = (role) => {
    setMarginConfig((prev) => {
      const cur = new Set(prev.visible_roles || []);
      if (role === "admin") return prev; // admin sempre attivo
      if (cur.has(role)) cur.delete(role); else cur.add(role);
      return { ...prev, visible_roles: Array.from(cur) };
    });
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const roles = Array.from(new Set(["admin", ...(marginConfig.visible_roles || [])]));
      const { data } = await api.put("/product-margins-config", { visible_roles: roles });
      setMarginConfig((prev) => ({ ...prev, visible_roles: data.visible_roles || roles }));
      toast.success("Visibilità margini aggiornata");
      setConfigOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingConfig(false); }
  };

  const formMargin = useMemo(() => {
    const b = parseFloat(form.buy_cost);
    const s = parseFloat(form.sell_cost);
    if (!Number.isFinite(b) || !Number.isFinite(s)) return null;
    return s - b;
  }, [form.buy_cost, form.sell_cost]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Package className="h-6 w-6 text-indigo-600" />
            Catalogo Prodotti
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Prodotti assicurativi collegati a Compagnia + Ramo. Precompilano ramo, provvigione ed
            {canSeeMargins ? " eventuali margini economici" : " economici (nascosti al tuo ruolo)"} nel form polizza/preventivo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setConfigOpen(true)} data-testid="margin-config-btn">
              <ShieldAlert className="h-4 w-4 mr-1.5 text-amber-600" /> Visibilità margini
            </Button>
          )}
          <Button data-testid="add-product-btn" onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Nuovo prodotto
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Cerca nome o codice..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="products-search" />
        <select className="h-9 px-2 border border-slate-300 rounded-md text-sm bg-white" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} data-testid="products-filter-company">
          <option value="">Tutte le compagnie</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="h-9 px-2 border border-slate-300 rounded-md text-sm bg-white" value={filterRamo} onChange={(e) => setFilterRamo(e.target.value)} data-testid="products-filter-ramo">
          <option value="">Tutti i rami</option>
          {taxRates.map((t) => <option key={t.ramo} value={t.ramo}>{ramoLabel(t.ramo)}</option>)}
        </select>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} prodotti</span>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Prodotto</th>
              <th className="px-3 py-2 text-left">Compagnia</th>
              <th className="px-3 py-2 text-left">Ramo</th>
              <th className="px-3 py-2 text-right">Provv. %</th>
              <th className="px-3 py-2 text-right">Provv. Broker %</th>
              {canSeeMargins && <th className="px-3 py-2 text-right bg-amber-50">Acquisto</th>}
              {canSeeMargins && <th className="px-3 py-2 text-right bg-amber-50">Vendita</th>}
              {canSeeMargins && <th className="px-3 py-2 text-right bg-amber-50">Margine</th>}
              <th className="px-3 py-2 text-center">Stato</th>
              <th className="px-3 py-2 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={canSeeMargins ? 10 : 7} className="px-3 py-6 text-center text-slate-500">Caricamento…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={canSeeMargins ? 10 : 7} className="px-3 py-6 text-center text-slate-500">Nessun prodotto. Crea il primo con &quot;Nuovo prodotto&quot;.</td></tr>}
            {filtered.map((p) => {
              const buy = Number(p.buy_cost);
              const sell = Number(p.sell_cost);
              const hasBoth = Number.isFinite(buy) && Number.isFinite(sell);
              const margin = hasBoth ? sell - buy : null;
              return (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`product-row-${p.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{p.name}</div>
                    {p.code && <div className="text-[11px] text-slate-500 font-mono">{p.code}</div>}
                    {p.description && <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{p.description}</div>}
                  </td>
                  <td className="px-3 py-2">{companyName(p.company_id)}</td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs">{ramoLabel(p.ramo)}</span></td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.default_commission_percent != null ? `${p.default_commission_percent}%` : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.default_broker_commission_percent != null ? `${p.default_broker_commission_percent}%` : "—"}</td>
                  {canSeeMargins && <td className="px-3 py-2 text-right tabular-nums text-rose-700" data-testid={`product-buy-${p.id}`}>{fmtEur(p.buy_cost)}</td>}
                  {canSeeMargins && <td className="px-3 py-2 text-right tabular-nums text-emerald-700" data-testid={`product-sell-${p.id}`}>{fmtEur(p.sell_cost)}</td>}
                  {canSeeMargins && (
                    <td className="px-3 py-2 text-right tabular-nums font-semibold" data-testid={`product-margin-${p.id}`}>
                      {margin === null ? "—" : (
                        <span className={margin >= 0 ? "text-emerald-700" : "text-rose-700"}>{fmtEur(margin)}</span>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 text-center">
                    {p.is_active ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs">Attivo</span> : <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">Disattivo</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nome prodotto *</Label>
              <Input data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Auto Formula Plus" />
            </div>
            <div>
              <Label>Compagnia *</Label>
              <select className="h-9 w-full px-2 border border-slate-300 rounded-md text-sm bg-white" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} data-testid="product-company">
                <option value="">Seleziona…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Ramo *</Label>
              <select className="h-9 w-full px-2 border border-slate-300 rounded-md text-sm bg-white" value={form.ramo} onChange={(e) => setForm({ ...form, ramo: e.target.value })} data-testid="product-ramo">
                <option value="">Seleziona…</option>
                {taxRates.map((t) => <option key={t.ramo} value={t.ramo}>{ramoLabel(t.ramo)}</option>)}
              </select>
            </div>
            <div>
              <Label>Codice interno</Label>
              <Input data-testid="product-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Es. AUTO-FP-2026" />
            </div>
            <div>
              <Label>Ordine</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
            </div>
            <div>
              <Label>Provvigione % (Agenzia)</Label>
              <Input type="number" step="0.01" min="0" max="100" value={form.default_commission_percent} onChange={(e) => setForm({ ...form, default_commission_percent: e.target.value })} placeholder="Es. 5.00" data-testid="product-commission-percent" />
            </div>
            <div>
              <Label>Provvigione % Broker</Label>
              <Input type="number" step="0.01" min="0" max="100" value={form.default_broker_commission_percent} onChange={(e) => setForm({ ...form, default_broker_commission_percent: e.target.value })} placeholder="Es. 2.50" />
            </div>

            {canSeeMargins && (
              <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2">
                <div className="flex items-center gap-2 text-amber-900 text-sm font-medium">
                  <TrendingUp className="h-4 w-4" /> Margini interni (visibili solo ai ruoli autorizzati)
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Costo di Acquisto €</Label>
                    <Input type="number" step="0.01" min="0" value={form.buy_cost} onChange={(e) => setForm({ ...form, buy_cost: e.target.value })} placeholder="Es. 45,00" data-testid="product-buy-cost" />
                  </div>
                  <div>
                    <Label>Costo di Vendita €</Label>
                    <Input type="number" step="0.01" min="0" value={form.sell_cost} onChange={(e) => setForm({ ...form, sell_cost: e.target.value })} placeholder="Es. 80,00" data-testid="product-sell-cost" />
                  </div>
                  <div>
                    <Label>Margine (auto)</Label>
                    <div className={`h-9 rounded-md border px-3 flex items-center text-sm font-semibold tabular-nums ${formMargin === null ? "text-slate-400 border-slate-200 bg-white" : formMargin >= 0 ? "text-emerald-700 border-emerald-200 bg-white" : "text-rose-700 border-rose-200 bg-white"}`} data-testid="product-margin-preview">
                      {formMargin === null ? "—" : fmtEur(formMargin)}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-amber-800/80">
                  Vendita − Acquisto = margine agenzia. Non è mai mostrato al cliente. Sarà auto-precompilato nelle polizze/preventivi che scelgono questo prodotto.
                </p>
              </div>
            )}

            <div className="col-span-2">
              <Label>Descrizione</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Note interne sul prodotto" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="prod-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <Label htmlFor="prod-active" className="cursor-pointer">Prodotto attivo (visibile nel dropdown polizza)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-product-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="h-4 w-4 mr-1.5" /> Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Configurazione Visibilità Margini (admin only) */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="margin-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Visibilità margini prodotto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Scegli quali ruoli possono vedere e modificare Costo di Acquisto, Costo di Vendita e Margine.
              I dati economici NON sono mai mostrati al cliente. <strong>Admin</strong> ha sempre accesso.
            </p>
            <div className="space-y-2">
              {(marginConfig.role_choices || []).map((role) => {
                const checked = (marginConfig.visible_roles || []).includes(role);
                const forced = role === "admin";
                return (
                  <label key={role} className={`flex items-center gap-2 rounded-md border px-3 py-2 ${forced ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200 hover:border-indigo-300 cursor-pointer"}`}>
                    <input
                      type="checkbox"
                      disabled={forced}
                      checked={checked}
                      onChange={() => toggleConfigRole(role)}
                      data-testid={`margin-role-${role}`}
                    />
                    <span className="text-sm font-medium">{ROLE_LABELS[role] || role}</span>
                    {forced && <span className="ml-auto text-[11px] text-slate-500">(sempre visibile)</span>}
                  </label>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)} disabled={savingConfig}>Annulla</Button>
            <Button onClick={saveConfig} disabled={savingConfig} data-testid="save-margin-config-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Save className="h-4 w-4 mr-1.5" /> Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
